<template>
    <Dialog v-model:open="opened">
        <DialogTrigger v-if="showTrigger" as-child>
            <slot name="trigger">
                <Button variant="ghost" size="sm">
                    {{ $t("integrations.detail_modal.learn_more") }}</Button
                >
            </slot>
        </DialogTrigger>
        <DialogContent class="sm:max-w-[548px]">
            <DialogGoBack></DialogGoBack>
            <DialogHeader :class="gradient">
                <template #icon> <slot name="icon" /></template>

                <DialogTitle><slot name="title" /></DialogTitle>
                <DialogDescription>
                    <slot name="short-description" />
                </DialogDescription>
            </DialogHeader>

            <div
                class="pr-6 mt-[18px] mb-[18px] grid"
                :class="insetDescription ? 'grid-cols-[96px_1fr]' : 'pl-6'"
            >
                <div v-if="insetDescription"></div>

                <div>
                    <p class="font-medium leading-6 tracking-[-0.01em]">
                        {{ $t("integrations.detail_modal.overview") }}
                    </p>

                    <p
                        class="text-sm leading-5 text-iron-500 mt-[7px] max-w-[420px]"
                    >
                        <slot name="description" />
                    </p>
                </div>
            </div>

            <!-- Custom content body slot (e.g., forms) -->
            <slot />
            <DialogFooter>
                <DialogClose>
                    <Button variant="outline">
                        {{ $t("integrations.detail_modal.close") }}</Button
                    >
                </DialogClose>

                <div
                    v-if="props.requiresSub && !hasSubscription"
                    class="relative"
                >
                    <Button
                        type="button"
                        class="gap-2"
                        @click.prevent="showProPayment"
                    >
                        <Plus :size="20" stroke-width="2.25" />
                        {{ $t("add") }}</Button
                    >
                    <svg
                        class="-right-2 -top-1 h-5 w-5 bg-white absolute rounded-full p-[2px]"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M3.33319 13.3332H12.6665M1.33319 2.6665L3.33319 10.6665H12.6665L14.6665 2.6665L10.6665 7.33317L7.99986 2.6665L5.33319 7.33317L1.33319 2.6665Z"
                            stroke="url(#paint0_linear_1067_2699)"
                            stroke-width="1.4"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <defs>
                            <linearGradient
                                id="paint0_linear_1067_2699"
                                x1="1.00018"
                                y1="1.99976"
                                x2="10.1364"
                                y2="14.5624"
                                gradientUnits="userSpaceOnUse"
                            >
                                <stop stop-color="#FF8D47" />
                                <stop offset="0.329612" stop-color="#FF8947" />
                                <stop offset="0.534043" stop-color="#FF4247" />
                                <stop offset="0.68692" stop-color="#E92A5B" />
                                <stop offset="0.828171" stop-color="#DD1C67" />
                                <stop offset="1" stop-color="#CF0283" />
                            </linearGradient>
                        </defs>
                    </svg>
                </div>

                <div v-else>
                    <slot name="cta" />
                </div>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogGoBack,
    DialogClose,
} from "@/components/ui/dialog-v2";
import { Plus } from "lucide-vue-next";
import { computed } from "vue";

import useProDialog from "@/ProModal/composables/useProDialog";
import { useHasSubscription } from "@/composables/useHasSubscription";

interface Props {
    requiresSub?: boolean;
    gradient?: string;
    gradientClass?: string;
    /** Aligns the overview text under the header title. Turn off when the dialog has a full-width body. */
    insetDescription?: boolean;
    showTrigger?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    requiresSub: true,
    insetDescription: true,
    showTrigger: true,
});

// Allow parent to control dialog visibility when needed
const opened = defineModel<boolean>("open", { default: false });

const gradient = computed(() => props.gradient || props.gradientClass || "");

const proDialog = useProDialog();
const { hasSubscription } = useHasSubscription();

async function showProPayment() {
    opened.value = false; // Close the detail dialog first
    await new Promise((resolve) => setTimeout(resolve, 150)); // Small delay for animation
    proDialog.show({
        feature: "integrations",
    });
}
</script>
